import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { handleApiErrors } from 'helpers';
import { useNewApiCall } from 'hooks';
import moment from 'moment';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { Divider } from 'primereact/divider';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';
import { SubmitHandler, useForm } from 'react-hook-form';
import { EmployeesSchedules, PersonAbsence, Shift } from 'types/interfaces';
import { DropdownWithDeletedValueTemplate } from 'utils/DropdownWithDeletedValueTemplate';

import { AbsenceService, EmployeesService } from 'components/services';
import { PersonAbsenceSchema } from 'constants/schemas';
import ConfirmationDialog from 'ui/shared/ConfirmationDialog';
import DeleteConfirmationDialog from 'ui/shared/DeleteConfirmationDialog';
import { withDialogFooterSpacing } from 'ui/shared/dialogFooterSpacing';

import '../../ui/css/AssignmentsAbsenceCard.css';

const nestedDialogStyle: React.CSSProperties = {
  width: 'min(600px, calc(100vw - 1rem))',
  maxWidth: 'calc(100vw - 1rem)',
  maxHeight: 'calc(100vh - 1rem)'
};

const nestedDialogContentStyle: React.CSSProperties = {
  maxHeight: 'calc(100vh - 8rem)',
  overflow: 'auto'
};

interface AbsenceDialogProps {
  absence: PersonAbsence | null;
  onSave: (absence: PersonAbsence) => void;
  onDelete: (absence: PersonAbsence) => void;
  onClose: () => void;
  departmentId: string;
  employeeId?: string;
  id?: number;
  ref: any;
  employeesShifts?: EmployeesSchedules | null;
  newShifts?: Shift[];
  updatedShifts?: Shift[];
}

interface RefInterface {
  handleCancel: () => void;
}

const AssignmentsAbsenceCard = React.forwardRef<
  RefInterface | null,
  AbsenceDialogProps
>(
  (
    {
      absence,
      onSave,
      onDelete,
      onClose,
      departmentId,
      employeeId,
      id,
      employeesShifts,
      newShifts,
      updatedShifts
    }: AbsenceDialogProps,
    ref
  ) => {
    const {
      handleSubmit,
      setValue,
      watch,
      getValues,
      setError,
      formState: { errors, isDirty }
    } = useForm<PersonAbsence>({
      resolver: zodResolver(PersonAbsenceSchema),
      defaultValues: absence || {
        id: id ?? 0,
        personId: employeeId ? parseInt(employeeId) : 0,
        date: new Date().toISOString().split('T')[0],
        from: new Date().toISOString(),
        to: new Date().toISOString(),
        absence: { id: 0, label: '', shouldRemoveShift: false },
        departmentId: parseInt(departmentId)
      }
    });

    const toast = useRef<Toast>(null);
    const [absenceTypes, setAbsenceTypes] = useState<any[]>([]);
    const [employees, setEmployees] = useState<any[]>([]);
    const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showRemoveShiftsDialog, setShowRemoveShiftsDialog] = useState(false);
    const [shiftsToRemove, setShiftsToRemove] = useState<any[]>([]);
    const [confirmedRemoveShifts, setConfirmedRemoveShifts] = useState(false);

    const from = watch('from');
    const to = watch('to');
    const guid = watch('guid');
    const absenceId = watch('id');
    const absenceTypeLabel = watch('absence.label');
    const unsavedAbsence = !guid?.startsWith('temp-');

    const getAbsenceCardApi = useCallback(async () => {
      if (!departmentId) {
        setAbsenceTypes([]);
        setEmployees([]);
        return;
      }
      try {
        const employees = (
          await EmployeesService.getByDepartments(parseInt(departmentId))
        ).data;
        const [absenceRes, empRes] = await Promise.all([
          AbsenceService.getAbsencesForSelection(),
          EmployeesService.getByDepartments(parseInt(departmentId))
        ]);
        setAbsenceTypes(absenceRes.data.value);
        setEmployees(
          empRes.data.value!.map((e: any) => ({ id: e.id, label: e.fullName }))
        );
      } catch (error) {
        console.error('Error fetching employees:', error);
        toast.current?.show({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Не удалось загрузить сотрудников'
        });
      }
    }, [departmentId]);

    useNewApiCall(getAbsenceCardApi, [departmentId]);

    const findAffectedShifts = useCallback(() => {
      const personId = getValues('personId');
      const absenceStart = moment(getValues('from')).toDate();
      const absenceEnd = moment(getValues('to')).toDate();

      const allShifts: any[] = [];

      const personSchedule = employeesShifts?.[String(personId)];
      if (personSchedule) {
        Object.values(personSchedule).forEach((day: any) =>
          day.forEach((shift: any) => allShifts.push(shift))
        );
      }

      if (newShifts)
        allShifts.push(...newShifts.filter((s) => s.personId === personId));
      if (updatedShifts)
        allShifts.push(...updatedShifts.filter((s) => s.personId === personId));

      const affected = allShifts.filter((s) => {
        const shiftDate =
          s.date || (s.start ? moment(s.start).format('YYYY-MM-DD') : '');
        const startTime = s.workdayStart || moment(s.start).format('HH:mm');
        const [h, m] = (s.workdayDuration || '00:00').split(':').map(Number);

        const shiftStart = moment(
          `${shiftDate} ${startTime}`,
          'YYYY-MM-DD HH:mm'
        ).toDate();
        const shiftEnd = moment(shiftStart)
          .add({ hours: h, minutes: m })
          .toDate();

        const overlap = shiftStart < absenceEnd && shiftEnd > absenceStart;

        if (overlap) {
          console.log('⚠️ пересечение:', {
            shiftDate,
            start: moment(shiftStart).format('YYYY-MM-DD HH:mm'),
            end: moment(shiftEnd).format('YYYY-MM-DD HH:mm')
          });
        }

        return overlap;
      });

      console.log('🧩 Найдено пересечений:', affected.length);
      return affected;
    }, [employeesShifts, newShifts, updatedShifts]);

    useEffect(() => {
      if (confirmedRemoveShifts) {
        setShowRemoveShiftsDialog(false);
        handleSubmit(onSubmit)();
        setConfirmedRemoveShifts(false);
      }
    }, [confirmedRemoveShifts]);

    const onSubmit: SubmitHandler<PersonAbsence> = async (data) => {
      const shouldRemoveShift = data.absence?.shouldRemoveShift;

      if (shouldRemoveShift && !confirmedRemoveShifts) {
        const affected = findAffectedShifts();
        if (affected.length > 0) {
          setShiftsToRemove(affected);
          setShowRemoveShiftsDialog(true);
          return;
        }
      }

      const from = new Date(data.from);
      const to = new Date(data.to);

      if (from >= to) {
        toast.current?.show({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Время начала должно быть раньше окончания'
        });
        return;
      }

      const formatted: PersonAbsence = {
        ...data,
        id: absenceId,
        date: from.toISOString().split('T')[0]
      };

      try {
        const validationResult = await AbsenceService.createPersonAbsence(
          formatted.personId,
          formatted,
          true // validation = true
        );
        if (validationResult?.data.isSuccess) {
          onSave(formatted);
        } else {
          handleApiErrors({
            error: validationResult?.data?.error,
            setError,
            fieldMapping: {
              AlreadyExists: {
                field: 'from',
                message: 'Обнаружено пересечение с другим отсутствием'
              },
              AbsenceConflict: {
                field: 'from',
                message: 'Обнаружено пересечение с другим отсутствием'
              }
            },
            customHandlers: {
              AlreadyExists: () =>
                showAbsenceConflictDetails(
                  (validationResult?.data as any)?.errorObject
                ),
              AbsenceConflict: () =>
                showAbsenceConflictDetails(
                  (validationResult?.data as any)?.errorObject
                )
            }
          });
        }
      } catch (error: any) {
        toast.current?.show({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Неизвестная ошибка при валидации'
        });
      }
    };

    const showAbsenceConflictDetails = (conflicts: any[] | undefined) => {
      const conflictList = Array.isArray(conflicts)
        ? conflicts
        : conflicts
        ? [conflicts]
        : [];

      if (conflictList.length === 0) {
        setError('from', {
          type: 'manual',
          message: 'Обнаружен конфликт отсутствий'
        });
        return;
      }

      let conflictDetails = 'Обнаружены конфликтующие отсутствия:\n';
      conflictList.forEach((conflict, index) => {
        const start = moment(conflict.from).format('DD.MM.YYYY HH:mm');
        const end = moment(conflict.to).format('DD.MM.YYYY HH:mm');

        conflictDetails += `\n${index + 1}. ${start} - ${end}`;
      });

      setError('from', {
        type: 'manual',
        message: `Найдено ${conflictList.length} конфликтующих отсутствий`
      });

      toast.current?.show({
        severity: 'warn',
        summary: 'Конфликт отсутствий',
        detail: conflictDetails,
        life: 15000
      });
    };
    const handleCancel = () => {
      if (isDirty) {
        setShowUnsavedDialog(true);
      } else {
        onClose();
      }
    };

    useImperativeHandle(ref, () => ({ handleCancel }));

    const handleDelete = () => {
      setShowDeleteDialog(true);
    };

    const confirmDelete = () => {
      onDelete(getValues());
      onClose();
    };

    return (
      <div className="dialog-tabless-card calendarAssingment-absence-card calendarAssingment-absence-card__roles">
        <Toast ref={toast} />
        <Divider />

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-row gap-5">
            <div className="w-full">
              <div className="flex flex-row justify-content-between align-items-center gap-3 w-full">
                <label htmlFor="employee" className="absence-label">
                  Сотрудник<span className="required">*</span>:
                </label>
                <div className="input-container">
                  <Dropdown
                    id="employee"
                    value={watch('personId')}
                    options={employees}
                    onChange={(e) =>
                      setValue('personId', e.value, { shouldDirty: true })
                    }
                    optionLabel="label"
                    optionValue="id"
                    placeholder="Выберите сотрудника"
                    filter
                    resetFilterOnHide
                    panelStyle={{ width: '100px' }}
                    className={`absence-type-dropdown ${
                      errors.personId ? 'p-invalid' : ''
                    }`}
                    panelClassName="assignmentsAbsenceCard-dropdown--panel"
                  />
                </div>
              </div>
              <p
                className={`task-type-card-errors card-errors ${
                  errors.personId ? 'text-red-500' : 'text-transparent'
                }`}
              >
                {errors.personId ? (errors.personId.message as string) : ' '}
              </p>
            </div>
          </div>

          <div className="flex flex-row gap-5">
            <div className="w-full">
              <div className="flex flex-row justify-content-between align-items-center gap-3 w-full">
                <label htmlFor="absenceType" className="absence-label">
                  Вид отсутствия<span className="required">*</span>:
                </label>
                <div className="input-container">
                  <Dropdown
                    id="absenceType"
                    value={watch('absence').id}
                    options={absenceTypes}
                    onChange={(e) => {
                      const selected = absenceTypes.find(
                        (a) => a.id === e.value
                      );

                      setValue(
                        'absence',
                        {
                          id: e.value,
                          label: selected?.label || '',
                          shortName: selected?.shortName,
                          shouldRemoveShift:
                            selected?.shouldRemoveShift ?? false
                        },
                        { shouldDirty: true }
                      );
                    }}
                    optionLabel="label"
                    optionValue="id"
                    placeholder={
                      watch('absence').id
                        ? absenceTypeLabel
                        : 'Выберите вид отсутствия'
                    }
                    filter
                    resetFilterOnHide
                    panelStyle={{ width: '100px' }}
                    panelClassName="assignmentsAbsenceCard-dropdown--panel"
                    className={`absence-type-dropdown ${
                      errors.absence?.id ? 'p-invalid' : ''
                    }`}
                    valueTemplate={(option) => (
                      <DropdownWithDeletedValueTemplate
                        option={option}
                        selectedItem={
                          watch('absence')?.id
                            ? { label: watch('absence').label }
                            : null
                        }
                        placeholder="Выберите вид отсутствия"
                        tooltipText="Вид отсутствия удалён в справочнике"
                      />
                    )}
                  />
                  <Tooltip target=".deleted-status-tooltip" />
                </div>
              </div>
              <p
                className={`task-type-card-errors card-errors ${
                  errors.absence?.id ? 'text-red-500' : 'text-transparent'
                }`}
              >
                {errors.absence?.id
                  ? (errors.absence.id.message as string)
                  : ' '}
              </p>
            </div>
          </div>

          <div className="flex flex-row gap-5">
            <div className="w-full">
              <div className="flex flex-row justify-content-between align-items-center gap-3 w-full">
                <label htmlFor="from" className="absence-label">
                  Начало<span className="required">*</span>:
                </label>
                <div className="input-container">
                  <Calendar
                    id="from"
                    value={from ? new Date(from) : null}
                    onChange={(e) => {
                      let val: any = e.value;
                      if (Array.isArray(val)) val = val[0] ?? null;
                      setValue(
                        'from',
                        val
                          ? moment(val).utc().format('YYYY-MM-DDTHH:mm:ss.SSSZ')
                          : '',
                        {
                          shouldDirty: true,
                          shouldValidate: true
                        }
                      );
                    }}
                    showTime
                    className={`w-full ${errors.from ? 'p-invalid' : ''}`}
                  />
                </div>
              </div>
              <p
                className={`task-type-card-errors card-errors ${
                  errors.from ? 'text-red-500' : 'text-transparent'
                }`}
              >
                {errors.from ? (errors.from.message as string) : ' '}
              </p>
            </div>
          </div>

          <div className="flex flex-row gap-5">
            <div className="w-full">
              <div className="flex flex-row justify-content-between align-items-center gap-3 w-full">
                <label htmlFor="to" className="absence-label">
                  Конец<span className="required">*</span>:
                </label>
                <div className="input-container">
                  <Calendar
                    id="to"
                    value={to ? new Date(to) : null}
                    onChange={(e) => {
                      let val: any = e.value;
                      if (Array.isArray(val)) val = val[0] ?? null;
                      setValue(
                        'to',
                        val
                          ? moment(val).utc().format('YYYY-MM-DDTHH:mm:ss.SSSZ')
                          : '',
                        {
                          shouldDirty: true,
                          shouldValidate: true
                        }
                      );
                    }}
                    showTime
                    className={`w-full ${errors.to ? 'p-invalid' : ''}`}
                  />
                </div>
              </div>
              <p
                className={`task-type-card-errors card-errors ${
                  errors.to ? 'text-red-500' : 'text-transparent'
                }`}
              >
                {errors.to ? (errors.to.message as string) : ' '}
              </p>
            </div>
          </div>

          <div className="flex flex-row gap-5">
            <div className="w-full">
              <div className="flex flex-row justify-content-between align-items-start gap-3 w-full">
                <label htmlFor="comment" className="absence-label">
                  Комментарий сотрудника:
                </label>
                <div className="input-container">
                  <InputTextarea
                    id="comment"
                    value={watch('comment')}
                    disabled
                    className="w-full"
                    style={{ backgroundColor: '#f5f5f5' }}
                  />
                </div>
              </div>
              <p className="task-type-card-errors card-errors text-transparent">
                {' '}
              </p>
            </div>
          </div>

          <div className="default-card__buttons">
            <div className="flex gap-3">
              <Button
                type="submit"
                label={unsavedAbsence ? 'Применить' : 'Добавить отсутствие'}
                icon="pi pi-check"
                className={unsavedAbsence ? '' : 'p-button-success'}
              />
              {unsavedAbsence && (
                <Button
                  type="button"
                  label="Удалить"
                  icon="pi pi-trash"
                  className="p-button-danger"
                  onClick={handleDelete}
                />
              )}
              <Button
                type="button"
                label="Отмена"
                icon="pi pi-times"
                className="p-button-secondary"
                onClick={handleCancel}
              />
            </div>
          </div>
        </form>

        <Dialog
          header="Удаление смен"
          visible={showRemoveShiftsDialog}
          modal
          className="calendarAssignments-dialog calendarAssignments-dialog--confirm"
          style={nestedDialogStyle}
          contentStyle={nestedDialogContentStyle}
          onHide={() => setShowRemoveShiftsDialog(false)}
          pt={withDialogFooterSpacing()}
          footer={
            <div>
              <Button
                label="Отмена"
                icon="pi pi-times"
                onClick={() => setShowRemoveShiftsDialog(false)}
                className="p-button-secondary"
              />
              <Button
                label="Продолжить"
                icon="pi pi-check"
                onClick={() => {
                  setConfirmedRemoveShifts(true);
                }}
                className="p-button-danger"
              />
            </div>
          }
        >
          <p>Добавление отсутствия приведёт к удалению следующих смен:</p>
          <ul>
            {shiftsToRemove.map((s, i) => {
              const shiftDate =
                s.date || (s.start ? moment(s.start).format('YYYY-MM-DD') : '');
              return (
                <li key={i}>
                  {moment(shiftDate).format('DD.MM.YYYY')} — {s.workdayStart},
                  длительность {s.workdayDuration}
                </li>
              );
            })}
          </ul>
        </Dialog>

        <ConfirmationDialog
          visible={showUnsavedDialog}
          dialogStyle={nestedDialogStyle}
          onCancel={() => setShowUnsavedDialog(false)}
          onContinue={onClose}
        />

        <DeleteConfirmationDialog
          visible={showDeleteDialog}
          onHide={() => setShowDeleteDialog(false)}
          onConfirm={confirmDelete}
          width="min(500px, calc(100vw - 1rem))"
          entityType="отсутствие"
          entityName={absenceTypeLabel}
        />
      </div>
    );
  }
);

AssignmentsAbsenceCard.displayName = 'AssignmentsAbsenceCard';

export default AssignmentsAbsenceCard;
